import React from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { bindMapControlsViewportAnchor } from "../../hooks/useMapWrapAnchor";
import "./MapContainer.css";

export const USER_LOCATION_ZOOM = 13;

const LOCATE_ICON_SVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-cursor-fill" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M14.082 2.182a.5.5 0 0 1 .103.557L8.528 15.467a.5.5 0 0 1-.917-.007L5.57 10.694.803 8.652a.5.5 0 0 1-.006-.916l12.728-5.657a.5.5 0 0 1 .556.103z" />
    </svg>
`;

export default function MapZoomLocateControl({ onLocate, locateZoom = USER_LOCATION_ZOOM }) {
    const map = useMap();
    const onLocateRef = React.useRef(onLocate);
    onLocateRef.current = onLocate;

    React.useEffect(() => {
        const pendingRef = { current: false };

        const locate = L.DomUtil.create("a", "map-locate-control__btn");
        locate.href = "#";
        locate.setAttribute("role", "button");
        locate.setAttribute("aria-label", "Моё местоположение");
        locate.title = "Моё местоположение";
        locate.innerHTML = LOCATE_ICON_SVG;

        const zoomIn = L.DomUtil.create("a", "leaflet-control-zoom-in");
        zoomIn.href = "#";
        zoomIn.title = "Приблизить";
        zoomIn.setAttribute("role", "button");
        zoomIn.setAttribute("aria-label", "Приблизить");
        zoomIn.innerHTML = "+";

        const zoomOut = L.DomUtil.create("a", "leaflet-control-zoom-out");
        zoomOut.href = "#";
        zoomOut.title = "Отдалить";
        zoomOut.setAttribute("role", "button");
        zoomOut.setAttribute("aria-label", "Отдалить");
        zoomOut.innerHTML = "&minus;";

        const handleLocateClick = (event) => {
            L.DomEvent.preventDefault(event);
            L.DomEvent.stopPropagation(event);
            if (!navigator.geolocation || pendingRef.current) return;

            pendingRef.current = true;
            L.DomUtil.addClass(locate, "leaflet-disabled");
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const lat = Number(position?.coords?.latitude);
                    const lng = Number(position?.coords?.longitude);
                    if (Number.isFinite(lat) && Number.isFinite(lng)) {
                        map.flyTo([lat, lng], locateZoom, { duration: 0.8 });
                        onLocateRef.current?.(lat, lng);
                    }
                    pendingRef.current = false;
                    L.DomUtil.removeClass(locate, "leaflet-disabled");
                },
                () => {
                    pendingRef.current = false;
                    L.DomUtil.removeClass(locate, "leaflet-disabled");
                },
                { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
            );
        };

        const handleZoomIn = (event) => {
            L.DomEvent.preventDefault(event);
            L.DomEvent.stopPropagation(event);
            map.zoomIn();
        };

        const handleZoomOut = (event) => {
            L.DomEvent.preventDefault(event);
            L.DomEvent.stopPropagation(event);
            map.zoomOut();
        };

        L.DomEvent.disableClickPropagation(locate);
        L.DomEvent.disableClickPropagation(zoomIn);
        L.DomEvent.disableClickPropagation(zoomOut);
        L.DomEvent.on(locate, "click", handleLocateClick);
        L.DomEvent.on(zoomIn, "click", handleZoomIn);
        L.DomEvent.on(zoomOut, "click", handleZoomOut);

        const control = new L.Control({ position: "bottomright" });
        control.onAdd = () => {
            const container = L.DomUtil.create("div", "leaflet-bar leaflet-control map-zoom-locate-control");
            container.appendChild(locate);
            container.appendChild(zoomIn);
            container.appendChild(zoomOut);
            return container;
        };
        control.addTo(map);

        return () => {
            L.DomEvent.off(locate, "click", handleLocateClick);
            L.DomEvent.off(zoomIn, "click", handleZoomIn);
            L.DomEvent.off(zoomOut, "click", handleZoomOut);
            control.remove();
        };
    }, [locateZoom, map]);

    React.useLayoutEffect(() => {
        const anchorEl = map.getContainer().closest(".map-wrap, .new-spot-map") || map.getContainer().parentElement;
        return bindMapControlsViewportAnchor(anchorEl);
    }, [map]);

    return null;
}
